import React from "react";
import { Paper, Typography, Tooltip, makeStyles } from "@material-ui/core";
import { useUser } from "context/userContext";
import grammarSimConstants from "constants/Grammar/SimulateGrammar";

/**
 * Paleta de cores utilizada na renderização da árvore de derivação.
 *
 * @constant
 * @type {Object}
 */
export const TREE_COLORS = {
  ACCEPTED_BORDER: "#4caf50",
  ACCEPTED_BG: "#e8f5e9",
  REJECTED_BORDER: "#f44336",
  REJECTED_BG: "#ffebee",
  INTERMEDIATE_BORDER: "#2196f3",
  INTERMEDIATE_BG: "#e3f2fd",
  CONNECTOR_LINE: "#bdbdbd",
  TEXT_COLOR: "#000000",
};

/**
 * Estilos utilizados pelo componente de árvore de derivação.
 *
 * Os conectores entre os nós são desenhados com pseudo-elementos CSS,
 * permitindo representar visualmente a relação pai-filho entre as etapas
 * da derivação.
 *
 * @returns {Object} Objeto contendo as classes CSS geradas pelo Material-UI.
 */
const useStyles = makeStyles(() => ({
  treeRoot: {
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
  },

  nodeContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    position: "relative",
    padding: "20px 12px 0 12px",
  },

  paperNode: {
    padding: "8px 16px",
    borderRadius: "8px",
    border: "2px solid",
    minWidth: "100px",
    textAlign: "center",
    color: TREE_COLORS.TEXT_COLOR,
    cursor: "grab",
    position: "relative",
    zIndex: 2,
    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.12)",
    backgroundClip: "padding-box",
  },

  ACCEPTED: {
    borderColor: TREE_COLORS.ACCEPTED_BORDER,
    backgroundColor: TREE_COLORS.ACCEPTED_BG,
  },

  REJECTED: {
    borderColor: TREE_COLORS.REJECTED_BORDER,
    backgroundColor: TREE_COLORS.REJECTED_BG,
  },

  INTERMEDIATE: {
    borderColor: TREE_COLORS.INTERMEDIATE_BORDER,
    backgroundColor: TREE_COLORS.INTERMEDIATE_BG,
  },

  childrenWrapper: {
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    position: "relative",
    marginTop: "28px",

    "&::before": {
      content: '""',
      position: "absolute",
      top: "-14px",
      left: "50%",
      width: "2px",
      height: "14px",
      backgroundColor: TREE_COLORS.CONNECTOR_LINE,
      transform: "translateX(-50%)",
    },
  },

  childrenRow: {
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    position: "relative",
  },

  childBranch: {
    position: "relative",
    display: "flex",
    justifyContent: "center",

    "&::before": {
      content: '""',
      position: "absolute",
      top: "-14px",
      left: 0,
      right: 0,
      height: "2px",
      backgroundColor: TREE_COLORS.CONNECTOR_LINE,
    },

    "&::after": {
      content: '""',
      position: "absolute",
      top: "-14px",
      left: "50%",
      width: "2px",
      height: "14px",
      backgroundColor: TREE_COLORS.CONNECTOR_LINE,
      transform: "translateX(-50%)",
    },

    "&:first-child::before": {
      left: "50%",
    },

    "&:last-child::before": {
      right: "50%",
    },

    "&:only-child::before": {
      display: "none",
    },
  },
}));

/**
 * Representa uma etapa da árvore de derivação.
 *
 * @typedef {Object} GrammarStep
 * @property {number|string} stepId Identificador único da etapa.
 * @property {number|string|null} parentId Identificador da etapa pai.
 * @property {string} [status] Status da etapa. Pode ser "ACCEPTED", "REJECTED" ou outro valor intermediário.
 * @property {string} [ruleApplied] Regra gramatical aplicada na etapa.
 * @property {string} [currentSententialForm] Forma sentencial resultante da etapa.
 */

/**
 * Renderiza recursivamente uma árvore de derivação de gramática.
 *
 * O componente recebe uma lista linear de etapas, identifica os filhos de cada
 * nó por meio da relação `parentId === currentId` e renderiza a árvore de forma
 * recursiva. Cada nó apresenta a forma sentencial da etapa e utiliza cores
 * diferentes para indicar aceitação, rejeição ou estado intermediário.
 *
 * Quando o nó possui filhos, linhas conectoras são desenhadas entre o nó pai e
 * os nós filhos, melhorando a visualização da estrutura da derivação.
 *
 * @component
 * @author Willian Brandao
 *
 * @param {Object} props Propriedades do componente.
 * @param {GrammarStep[]} props.steps Lista de etapas da derivação.
 * @param {number|string|null} [props.currentId=null] Identificador do nó atual. Quando `null`, o componente renderiza a raiz lógica da árvore.
 *
 * @returns {JSX.Element|null} Árvore de derivação renderizada ou `null` caso os dados sejam inválidos.
 */
const GrammarStepTree = ({ steps, currentId = null }) => {
  const classes = useStyles();
  const { language } = useUser();

  if (!steps || !Array.isArray(steps) || steps.length === 0) {
    return null;
  }

  const safeSteps = steps.filter((item) => item && typeof item === "object");

  if (safeSteps.length === 0) {
    return null;
  }

  const ruleLabelText = grammarSimConstants.tree.ruleLabel[language];
  const startText = grammarSimConstants.tree.start[language];

  const children = safeSteps.filter((item) => item.parentId === currentId);

  const currentNode =
    currentId !== null
      ? safeSteps.find((item) => item.stepId === currentId) || null
      : null;

  let nodeStatusClass = classes.INTERMEDIATE;
  let ruleText = startText;
  let sententialForm = "";

  if (currentNode) {
    if (currentNode.status === "ACCEPTED") {
      nodeStatusClass = classes.ACCEPTED;
    } else if (currentNode.status === "REJECTED") {
      nodeStatusClass = classes.REJECTED;
    }

    ruleText = currentNode.ruleApplied || startText;
    sententialForm = currentNode.currentSententialForm || "";
  }

  const displaySententialForm = sententialForm !== "" ? sententialForm : "-";

  /**
   * Renderiza o nó atual da árvore.
   *
   * @returns {JSX.Element|null} Elemento visual do nó atual ou `null` quando não houver nó atual.
   */
  const renderNode = () => {
    if (!currentNode) {
      return null;
    }

    return (
      <Paper className={classes.paperNode + " " + nodeStatusClass}>
        <Tooltip
          title={ruleLabelText + ": " + ruleText}
          arrow
          disableInteractive
        >
          <Typography variant="body2" style={{ fontWeight: "bold" }}>
            {displaySententialForm}
          </Typography>
        </Tooltip>
      </Paper>
    );
  };

  /**
   * Renderiza recursivamente os filhos do nó atual.
   *
   * @returns {JSX.Element|null} Subárvore dos filhos ou `null` quando o nó não possui filhos.
   */
  const renderChildren = () => {
    if (children.length === 0) {
      return null;
    }

    return (
      <div className={classes.childrenWrapper}>
        <div className={classes.childrenRow}>
          {children.map((child) => {
            if (!child || child.stepId === undefined || child.stepId === null) {
              return null;
            }

            return (
              <div className={classes.childBranch} key={child.stepId}>
                <GrammarStepTree steps={safeSteps} currentId={child.stepId} />
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className={classes.treeRoot}>
      <div className={classes.nodeContainer}>
        {renderNode()}
        {renderChildren()}
      </div>
    </div>
  );
};

export default GrammarStepTree;
